import { Component, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { CnaeService } from "../../services/external/cnae/cnae.service";
import { DialogService } from "../../services/internal/dialog/dialog.service";
import { ScrollService } from "../../services/internal/scroll/scroll.service";
import { CnpjAndCnaeMccResponseDTO } from "../simulation-economical-group/models/cnpj-and-cnae-mcc-response-dto.model";
import { FormBase } from "../form-base";
import { InputType } from "src/app/shared/fd-form-components/fd-input/fd-input.component";
import { FdFieldConfigs } from "src/app/shared/shared-components.module";
import { alphanumericCnpjMask } from "../../masks/document-masks";
import { CnpjAlphanumericValidator } from "../../validators/cpf-cnpj-validator";

@Component({
    selector: "app-cnpj-and-cnae-search",
    templateUrl: "./cnpj-and-cnae-search.component.html",
    styleUrls: ["./cnpj-and-cnae-search.component.scss"],
    standalone: false,
})
export class CnpjAndCnaeSearchComponent extends FormBase implements OnInit {
    formGroup: UntypedFormGroup = this.createFormGroup();
    fields: FdFieldConfigs = this.createFields();

    isLoading = false;
    hasError = false;
    isEmpty = false;
    result: CnpjAndCnaeMccResponseDTO | null = null;
    readonly NOT_APPLICABLE_ABECS = 'Não se aplica';

    constructor(
        private formBuilder: UntypedFormBuilder,
        protected scrollService: ScrollService,
        private cnaeService: CnaeService,
        private dialogService: DialogService,
    ) {
        super(scrollService);
    }

    ngOnInit() {
        this.formControls.cnpj.setValue("");
        this.formControls.cnae.setValue("");
    }

    getMccsByCnpjAndCnae() {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const cnpj = (this.formControls.cnpj.value as string).trim();
        const cnae = (this.formControls.cnae.value as string).trim();

        this.isLoading = true;
        this.hasError = false;
        this.isEmpty = false;
        this.result = null;

        this.cnaeService.getCnpjAndCnaeMccInfo(cnpj, cnae).toPromise().then((res) => {
            this.isLoading = false;

            // response pode vir como array (T[]) ou objeto único (T) dependendo da versão do Pageable
            const raw = res?.response;
            const items: CnpjAndCnaeMccResponseDTO[] = Array.isArray(raw)
                ? raw
                : raw != null ? [raw] : [];

            if (items.length === 0) {
                this.isEmpty = true;
                return;
            }

            this.result = items[0];
        }).catch(() => {
            this.isLoading = false;
            this.hasError = true;
        });
    }

    protected createFormGroup(): UntypedFormGroup {
        return this.formBuilder.group({
            cnpj: ["", [Validators.required, CnpjAlphanumericValidator]],
            cnae: ["", [Validators.required, Validators.pattern(/^\d+$/)]],
        });
    }

    createFields(): FdFieldConfigs {
        return {
            cnpj: {
                label: "CNPJ",
                controlName: "cnpj",
                type: InputType.TEL,
                isNumber: true,
                mask: alphanumericCnpjMask,
                maskCharsReplace: /[./ -]/g,
                messages: {
                    required: "Informe o CNPJ",
                    pattern: "CNPJ deve conter apenas números",
                },
            },
            cnae: {
                label: "CNAE",
                controlName: "cnae",
                type: InputType.TEL,
                isNumber: true,
                messages: {
                    required: "Informe o CNAE",
                    pattern: "CNAE deve conter apenas números",
                },
            },
        };
    }

    get formControls(): { [key: string]: AbstractControl } {
        return this.formGroup.controls;
    }

    formatNullable(value: number | string | null | undefined): string {
        return value != null ? String(value) : "";
    }

    formatMccAbecsNumberAndDescription(mccNumber: number | null, mccDescription: string | null): string {
        return mccNumber != null
            ? this.formatNullable(mccNumber) + " - " + this.formatNullable(mccDescription)
            : this.NOT_APPLICABLE_ABECS;
    }

    formatMccCnaeNumberAndDescription(mccNumber: number | null, mccDescription: string | null): string {
        let mccCnaeDescriptionSplitted = this.formatNullable(mccDescription).split("-");
        let mccCnaeDescription = mccCnaeDescriptionSplitted.length > 1 ? mccCnaeDescriptionSplitted[1] : "";
        return this.formatNullable(mccNumber) + " - " + mccCnaeDescription;
    }
}
